import { useState } from 'react';

import { FontAwesomeIcon } from '@appcharge/shared-ui';
import { useFormik } from 'formik';
import * as yup from 'yup';

import Button from 'components/Button/Button';
import Input from 'components/Input/Input';
import { EModalState } from 'components/LoginModal/LoginModal.types';
import Typography from 'components/Typography/Typography';

import useUrlQuery from '../../hooks/useUrlQuery';
import { required } from '../../utils/errorsTextHelper';

import { FinishAccountFormProps } from './FinishAccountForm.types';

import './style.scss';

const FinishAccountForm = ({
  setNewPassword,
  setModalState
}: FinishAccountFormProps) => {
  const token = useUrlQuery('token');
  const email = useUrlQuery('email');

  let formSchema = yup.object().shape({
    password1: yup
      .string()
      .label('Password')
      .password()
      .required(required('Password')),
    password2: yup
      .string()
      .label('Password')
      .oneOf([yup.ref('password1'), null], 'Passwords must match')
      .required()
  });
  const {
    values,
    errors,
    touched,
    dirty,
    handleChange,
    handleBlur,
    submitForm,
    isValid
  } = useFormik({
    initialValues: {
      email: email!,
      password1: '',
      password2: ''
    },
    validationSchema: formSchema,
    onSubmit: async (values) => {
      setIsLoading(true);
      const data = {
        email: email!.toLowerCase(),
        rawNewPassword: values.password1,
        token
      };
      setNewPassword(data, (success: boolean) => {
        setIsLoading(false);
        if (success) setModalState(EModalState.LOGIN);
      });
    }
  });

  const [isLoading, setIsLoading] = useState(false);
  const [showPassword1, setShowPassword1] = useState(false);
  const [showPassword2, setShowPassword2] = useState(false);

  return (
    <div className="w-full">
      <div className="flex flex-col gap-y-2 text-center mb-6">
        <Typography tag="h4">Create your Password</Typography>
        <Typography className="text-p-s text-base-mutedForeground">
          Choose your passwords for logging in to AppCharge's Dashboard.
        </Typography>
      </div>
      <form
        className="flex flex-col gap-y-4"
        onSubmit={(e: React.SyntheticEvent) => {
          e.preventDefault();
        }}
        autoComplete="off"
      >
        <Input
          label="Email"
          placeholder="Email"
          name="email"
          value={email || ''}
          disabled
          autoComplete="off"
        />
        <div>
          <Input
            rightIcon={
              <FontAwesomeIcon
                onClick={() => setShowPassword1((prev) => !prev)}
                icon={
                  showPassword1 ? 'fa-solid fa-eye' : 'fa-solid fa-eye-slash'
                }
              />
            }
            type={showPassword1 ? 'text' : 'password'}
            label="New Password"
            placeholder="New Password"
            name="password1"
            value={values.password1}
            onChange={handleChange}
            onBlur={handleBlur}
            state={errors.password1 && touched.password1 ? 'error' : 'default'}
            description={
              errors.password1 && touched.password1 ? errors.password1 : ''
            }
            autoComplete="off"
          />
          <Typography className="mt-2 text-p-s text-base-mutedForeground">
            Your password must be at least 8 characters long and contain at
            least one capital letter (A-Z), one lowercase letter (a-z), one
            numeral (0-9) and at least one special symbol (e.g., !, @, #, $, %,
            etc.).
          </Typography>
        </div>
        <Input
          label="Confirm Password"
          placeholder="Confirm Password"
          name="password2"
          rightIcon={
            <FontAwesomeIcon
              onClick={() => setShowPassword2((prev) => !prev)}
              icon={showPassword2 ? 'fa-solid fa-eye' : 'fa-solid fa-eye-slash'}
            />
          }
          type={showPassword2 ? 'text' : 'password'}
          value={values.password2}
          onChange={handleChange}
          onBlur={handleBlur}
          state={errors.password2 && touched.password2 ? 'error' : 'default'}
          description={
            errors.password2 && touched.password2 ? errors.password2 : ''
          }
          autoComplete="off"
        />
        <Button
          disabled={!!Object.keys(errors).length || !isValid || !dirty}
          type="submit"
          onClick={submitForm}
          className="w-full"
          isLoading={isLoading}
        >
          Send
        </Button>
      </form>
      <Typography
        className="text-p-s cursor-pointer hover:opacity-80 underline text-center mt-9"
        onClick={() => setModalState(EModalState.LOGIN)}
      >
        Go back to login page
      </Typography>
    </div>
  );
};

export default FinishAccountForm;
